import { useState, FC, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { MenuItem } from '@mui/material';
import { Box, Fade, ButtonGroup, Button, Grid, Typography } from '@mui/material';
import { Close, Restore } from '@mui/icons-material';
import { Modal, Loader, SMBSelect } from '../../../components';
import { postTestContract } from '../../../fetch';
import { getAgents } from '../../../fetch/lookups';
import { IBuild, IPeople } from '../../../models';

interface ITestContractModal {
  currentBuild: IBuild;
  open: boolean;
  onClose: () => void;
  fetchBuilds: () => void;
}

export const TestContractModal: FC<ITestContractModal> = ({
  open,
  onClose,
  currentBuild,
  fetchBuilds
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isGenerating, setGenerating] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agents, setAgents] = useState<IPeople[] | null>(null);
  const [loadingAgents, setLoadingAgents] = useState(false);

  const fetchAgents = async () => {
    setLoadingAgents(true);
    try {
      const res = await getAgents(true);
      setAgents(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading agents, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingAgents(false);
    }
  };

  useEffect(() => {
    if (currentBuild) {
      fetchAgents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBuild]);

  return (
    <Modal open={open} onClose={onClose} maxWidth="sm">
      <Fade in={open}>
        <div>
          {isGenerating && (
            <Loader position="centered" type="overlay">
              Generating...
            </Loader>
          )}
          <Box marginBottom="1rem">
            {currentBuild && (
              <Typography variant="h5">
                Test Contract for {currentBuild.neighborhood} Phase{' '}
                {currentBuild.phase ? currentBuild.phase : 'no phase'} Lot {currentBuild.lotNumber}
              </Typography>
            )}
          </Box>
          <Grid container alignItems="center">
            <SMBSelect
              name="agent"
              label="Agent"
              variant="outlined"
              handleClear={() => setSelectedAgent('')}
              value={selectedAgent}
              onChange={e => {
                setSelectedAgent(e.target.value);
              }}
            >
              {agents &&
                agents.map((agent, index) => {
                  return (
                    <MenuItem key={`${index}`} value={agent.personId}>
                      {agent.lastName && `${agent.lastName}, ${agent.firstName}`}
                      {agent.workplace && ` - ${agent.workplace}`}
                    </MenuItem>
                  );
                })}
            </SMBSelect>
          </Grid>
          <Box mt={1} display="flex" alignItems="center" justifyContent="flex-end">
            <ButtonGroup>
              {currentBuild && (
                <Button
                  color={'primary'}
                  variant="contained"
                  disabled={isGenerating || loadingAgents || !selectedAgent}
                  startIcon={<Restore />}
                  onClick={async () => {
                    setGenerating(true);
                    try {
                      await postTestContract(currentBuild.buildId);

                      enqueueSnackbar(`Contract Generated!`, {
                        variant: 'success'
                      });
                      onClose();
                      fetchBuilds();
                    } catch (error) {
                      enqueueSnackbar(
                        error?.Detail ?? 'Error generating test contract, please try again.',
                        {
                          variant: 'error'
                        }
                      );
                    } finally {
                      setSelectedAgent(null);
                      setGenerating(false);
                    }
                  }}
                >
                  Generate
                </Button>
              )}
              <Button color="inherit" variant="contained" startIcon={<Close />} onClick={onClose}>
                Cancel
              </Button>
            </ButtonGroup>
          </Box>
        </div>
      </Fade>
    </Modal>
  );
};
